import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CloseIcon from '@mui/icons-material/Close';
import apiService, { resolveFileUrl } from '../services/api';

// 科技蓝青配色方案（与登录页保持一致）
const COLORS = {
  primary: '#3b82f6',
  secondary: '#06b6d4',
  light: '#60a5fa',
  text: '#f1f5f9',
  textMuted: '#94a3b8',
  bgCard: 'rgba(15, 23, 42, 0.95)',
  border: 'rgba(59, 130, 246, 0.25)',
  glow: 'rgba(59, 130, 246, 0.4)',
};

/**
 * 网站主页右下角浮动弹窗组件
 * 显示两个可配置的二维码悬浮按钮（标题与图片由管理员在后台配置）
 */
const FloatingWidgets = () => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);
  const [entered, setEntered] = useState(false);

  // 加载悬浮窗配置
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await apiService.getFloatingWidgets();
        if (mounted && res && res.success && Array.isArray(res.widgets)) {
          setWidgets(res.widgets);
        }
      } catch (e) {
        console.error('获取悬浮窗配置失败:', e);
      } finally {
        if (mounted) {
          setLoading(false);
          // 延迟入场，避免与页面主内容加载动画冲突
          setTimeout(() => mounted && setEntered(true), 600);
        }
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  // 仅显示已启用且配置了图片的悬浮窗
  const visibleWidgets = widgets
    .map((w, i) => ({ ...w, index: i }))
    .filter((w) => w.enabled && w.image);

  if (loading || visibleWidgets.length === 0) return null;

  const activeWidget = activeIndex !== null ? visibleWidgets.find((w) => w.index === activeIndex) : null;

  return (
    <Box
      sx={{
        position: 'fixed',
        right: { xs: 14, sm: 24 },
        bottom: { xs: 72, sm: 88 },
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '14px',
        opacity: entered ? 1 : 0,
        transform: entered ? 'translateX(0)' : 'translateX(30px)',
        transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* 展开的二维码卡片 */}
      {activeWidget && (
        <Box
          key={activeWidget.index}
          sx={{
            width: 224,
            p: '14px',
            background: COLORS.bgCard,
            backdropFilter: 'blur(24px)',
            border: `1px solid ${COLORS.border}`,
            borderRadius: '16px',
            boxShadow: `
              0 0 40px rgba(59, 130, 246, 0.2),
              0 16px 40px -12px rgba(0, 0, 0, 0.6)
            `,
            animation: 'widgetCardIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
            '@keyframes widgetCardIn': {
              '0%': { opacity: 0, transform: 'translateY(12px) scale(0.92)' },
              '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
            },
            position: 'relative',
          }}
        >
          {/* 关闭按钮 */}
          <Box
            onClick={() => setActiveIndex(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 22,
              height: 22,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              cursor: 'pointer',
              color: COLORS.textMuted,
              transition: 'all 0.2s',
              '&:hover': {
                color: '#fff',
                background: 'rgba(239, 68, 68, 0.25)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </Box>

          {/* 标题 */}
          <Typography
            sx={{
              color: COLORS.text,
              fontSize: '0.95rem',
              fontWeight: 700,
              textAlign: 'center',
              mb: 1.2,
              background: `linear-gradient(135deg, ${COLORS.text} 0%, ${COLORS.light} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              pr: 3,
            }}
          >
            {activeWidget.title}
          </Typography>

          {/* 二维码（白底） */}
          <Box
            sx={{
              mx: 'auto',
              width: 168,
              height: 168,
              background: '#fff',
              borderRadius: '12px',
              p: '8px',
              boxShadow: '0 0 24px rgba(59, 130, 246, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              src={resolveFileUrl(activeWidget.image)}
              alt={activeWidget.title}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </Box>

          <Typography
            sx={{
              color: COLORS.textMuted,
              fontSize: '0.72rem',
              textAlign: 'center',
              mt: 1.2,
            }}
          >
            扫一扫上面的二维码
          </Typography>
        </Box>
      )}

      {/* 悬浮按钮组（上下排列） */}
      {visibleWidgets.map((w, i) => {
        const isActive = activeIndex === w.index;
        return (
          <Tooltip key={w.index} title={w.title} arrow placement="left">
            <Box
              onClick={() => setActiveIndex(isActive ? null : w.index)}
              sx={{
                width: { xs: 48, sm: 54 },
                height: { xs: 48, sm: 54 },
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: isActive
                  ? `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`
                  : `linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(6, 182, 212, 0.85) 100%)`,
                border: `1px solid ${isActive ? COLORS.light : COLORS.border}`,
                boxShadow: isActive
                  ? `0 0 24px ${COLORS.glow}`
                  : '0 6px 20px rgba(0, 0, 0, 0.4)',
                color: '#fff',
                position: 'relative',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                animation: 'widgetFloat 3s ease-in-out infinite',
                animationDelay: `${i * 0.6}s`,
                '@keyframes widgetFloat': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-5px)' },
                },
                '&:hover': {
                  transform: 'scale(1.08)',
                  boxShadow: `0 0 30px ${COLORS.glow}, 0 8px 24px rgba(0, 0, 0, 0.5)`,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: -3,
                  borderRadius: '50%',
                  border: `1px solid ${COLORS.secondary}50`,
                  animation: 'widgetRing 2.4s ease-out infinite',
                  animationDelay: `${i * 1.2}s`,
                  pointerEvents: 'none',
                },
                '@keyframes widgetRing': {
                  '0%': { transform: 'scale(0.9)', opacity: 0.8 },
                  '100%': { transform: 'scale(1.45)', opacity: 0 },
                },
              }}
            >
              <QrCode2Icon sx={{ fontSize: { xs: 26, sm: 30 } }} />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default FloatingWidgets;
